import React, { useContext, useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toPng } from 'html-to-image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { ConfigContext } from '../shared/ConfigContext';
import { getLastFailedRequest } from '../../api/axiosSetup';
import { radius } from '../../themes/smartinventoryTheme';
import useDesignTokens from '../ui/useDesignTokens';
import eventBus from '../../utils/eventBus';
import { APP_VERSION } from '../../version';

// Route -> the one metered/limit feature that page's own meter shows, so the
// "Sent automatically" card can include the same live number instead of a
// generic "your plan" — matches FeatureUsageMeter's per-page feature keys.
const ROUTE_FEATURE = {
  invoices: 'invoices.issued.monthly',
  products: 'products.max',
  'client-orders': 'orders.monthly',
  coupons: 'coupons.active.max',
  advertisements: 'advertisements.active.max',
  reporting: 'reports.generated.monthly',
  users: 'users.seats',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');
const fmtTime = (ms) => (ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

const parseBrowser = (ua) => {
  if (!ua) return '';
  const osMatch = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : /iPhone|iPad/.test(ua) ? 'iOS' : '';
  const chrome = /Chrome\/(\d+)/.exec(ua);
  const firefox = /Firefox\/(\d+)/.exec(ua);
  const safari = !chrome && /Version\/(\d+).*Safari/.exec(ua);
  const edge = /Edg\/(\d+)/.exec(ua);
  let name = 'Browser';
  if (edge) name = `Edge ${edge[1]}`;
  else if (chrome) name = `Chrome ${chrome[1]}`;
  else if (firefox) name = `Firefox ${firefox[1]}`;
  else if (safari) name = `Safari ${safari[1]}`;
  return osMatch ? `${name} · ${osMatch}` : name;
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.5 }}>
    <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
      <strong>{label}:</strong> {value}
    </Typography>
  </Box>
);

// The "Message support" screen inside the help panel (step 9, screen 2c).
// `pageLabel`/`route` come from the panel so the "Sent automatically" card
// can name the current page without SupportForm needing its own routing.
// `capture` defaults to html-to-image's `toPng` — overridable so tests can
// inject a fake (html-to-image's named export is a read-only ES binding in
// this build, so `spyOn` cannot intercept it directly).
const SupportForm = ({ route, pageLabel, onSent, onCancel, capture = toPng }) => {
  const { t } = useTranslation('help');
  const { tokens } = useDesignTokens();
  const config = useContext(ConfigContext);
  const { plan, subscription, usage } = useEntitlements();
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const role = localStorage.getItem('userRole') || '';
  const username = localStorage.getItem('username') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const lastFailed = getLastFailedRequest();
  const featureKey = ROUTE_FEATURE[route];
  const featureUsage = featureKey ? usage(featureKey) : null;
  const renewsOn = fmtDate(subscription?.currentPeriodEnd);
  const browser = parseBrowser(navigator.userAgent);

  const captureScreenshot = async () => {
    setCapturing(true);
    setError('');
    try {
      const target = document.querySelector('main') || document.body;
      const dataUrl = await capture(target, { cacheBust: true, pixelRatio: 1 });
      setScreenshot(dataUrl);
    } catch (err) {
      console.error('Error capturing screenshot', err);
      setError(t('support.captureFailed', { defaultValue: "Couldn't capture the page — you can still send without one." }));
    } finally {
      setCapturing(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setError(t('support.messageRequired', { defaultValue: 'Tell us what happened first.' }));
      return;
    }
    setSending(true);
    setError('');
    try {
      const context = {
        route,
        role,
        companyId,
        plan: plan?.name || null,
        usage: featureUsage ? { feature: featureKey, used: featureUsage.used, limit: featureUsage.limit } : null,
        browser,
        lastError: lastFailed ? `${lastFailed.method} ${lastFailed.url} · ${lastFailed.status} · ${fmtTime(lastFailed.timestamp)}` : null,
        appVersion: APP_VERSION,
      };
      const body = { message: message.trim(), context };
      if (screenshot) body.screenshot = screenshot.split(',')[1] || null;

      await axios.post(`${config?.backendUrl}/support/messages`, body);
      eventBus.emit(eventBus.EVENTS.HELP_SUPPORT_SENT, { route });
      onSent();
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError(t('support.rateLimited', { defaultValue: "You've sent a few messages already — try again in a bit." }));
      } else {
        setError(err.response?.data?.error || t('support.sendFailed', { defaultValue: "Couldn't send your message. Please try again." }));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <TextField
        multiline
        minRows={4}
        fullWidth
        placeholder={t('support.whatsGoingOn', { defaultValue: "What's going on?" })}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: `${radius.md}px` } }}
      />

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        {t('support.attachScreenshot', { defaultValue: 'Attach a screenshot · optional' })}
      </Typography>
      {screenshot ? (
        <Box sx={{ position: 'relative', mb: 2, border: `1px solid ${tokens.border}`, borderRadius: `${radius.md}px`, overflow: 'hidden' }}>
          <Box component="img" src={screenshot} alt="" sx={{ display: 'block', width: '100%', maxHeight: 140, objectFit: 'cover' }} />
          <Button
            size="small"
            onClick={() => setScreenshot(null)}
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ position: 'absolute', top: 6, right: 6, minWidth: 0, bgcolor: 'background.paper' }}
          >
            {t('support.remove', { defaultValue: 'Remove' })}
          </Button>
        </Box>
      ) : (
        <Button
          fullWidth
          variant="outlined"
          onClick={captureScreenshot}
          disabled={capturing}
          startIcon={capturing ? <CircularProgress size={16} /> : <CameraAltOutlinedIcon fontSize="small" />}
          sx={{
            mb: 2, height: 44, borderStyle: 'dashed', borderColor: tokens.borderStrong, color: 'text.secondary',
          }}
        >
          {t('support.captureThisPage', { defaultValue: 'Capture this page' })}
        </Button>
      )}

      <Typography variant="overline" component="div" sx={{ color: 'text.disabled', mb: 0.5 }}>
        {t('support.sentAutomatically', { defaultValue: 'SENT AUTOMATICALLY' })}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <InfoRow label={t('support.page', { defaultValue: 'Page' })} value={pageLabel || route} />
        <InfoRow label={t('support.role', { defaultValue: 'Role' })} value={role} />
        {plan?.name && (
          <InfoRow
            label={t('support.plan', { defaultValue: 'Plan' })}
            value={featureUsage
              ? `${plan.name} · ${featureUsage.used}${featureUsage.limit != null ? ` / ${featureUsage.limit}` : ''}${renewsOn ? ` · ${t('support.renews', { date: renewsOn, defaultValue: `renews ${renewsOn}` })}` : ''}`
              : plan.name}
          />
        )}
        <InfoRow label={t('support.browser', { defaultValue: 'Browser' })} value={browser} />
        {lastFailed && (
          <InfoRow
            label={t('support.lastError', { defaultValue: 'Last error' })}
            value={`${lastFailed.method} ${lastFailed.url} · ${lastFailed.status} · ${fmtTime(lastFailed.timestamp)}`}
          />
        )}
        <InfoRow label={t('support.appVersion', { defaultValue: 'App version' })} value={APP_VERSION} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {username ? t('support.replyTo', { email: username, defaultValue: `Reply to ${username}` }) : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onCancel} disabled={sending}>{t('panel.back', { defaultValue: 'Back' })}</Button>
          <Button size="small" variant="contained" onClick={handleSend} disabled={sending}>
            {sending ? <CircularProgress size={16} color="inherit" /> : t('support.send', { defaultValue: 'Send' })}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SupportForm;
